/**
 * Session Tracking Service
 * Tracks user exploration within current session to provide fresh, personalized options
 * and avoid showing redundant suggestions
 */

import logger from '../utils/logger';

class SessionTrackingService {
  constructor() {
    this.sessionKey = 'compudopt_session_tracking';
    this.initializeSession();
  }

  /**
   * Initialize or retrieve session data
   */
  initializeSession() {
    try {
      const existing = sessionStorage.getItem(this.sessionKey);
      if (existing) {
        const data = JSON.parse(existing);
        // Check if session is still valid (same browser session)
        if (data.sessionId) {
          return;
        }
      }
    } catch (e) {
      logger.warn('[SessionTracking] Error loading session:', e);
    }

    // Create new session
    const newSession = {
      sessionId: this.generateSessionId(),
      startTime: new Date().toISOString(),
      exploredFlows: [],
      exploredOptions: [],
      conversationDepth: 0,
      primaryLanguage: 'en',
      exploreMoreClicks: 0,
      journeyPath: []
    };

    this.saveSession(newSession);
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current session data
   */
  getSession() {
    try {
      const data = sessionStorage.getItem(this.sessionKey);
      if (data) {
        return JSON.parse(data);
      }
    } catch (e) {
      logger.warn('[SessionTracking] Error parsing session:', e);
    }
    
    // Return default if not found
    return {
      sessionId: this.generateSessionId(),
      startTime: new Date().toISOString(),
      exploredFlows: [],
      exploredOptions: [],
      conversationDepth: 0,
      primaryLanguage: 'en',
      exploreMoreClicks: 0,
      journeyPath: []
    };
  }

  /**
   * Save session data
   */
  saveSession(sessionData) {
    try {
      sessionStorage.setItem(this.sessionKey, JSON.stringify(sessionData));
    } catch (e) {
      logger.warn('[SessionTracking] Error saving session:', e);
    }
  }

  /**
   * Persist a completed session's duration so the admin dashboard can show
   * REAL average session durations (localStorage ring, capped at 200).
   * Called from the beforeunload/pagehide session-end handlers.
   * @param {number} durationMs - Session duration in milliseconds
   */
  recordCompletedSession(durationMs) {
    if (!Number.isFinite(durationMs) || durationMs < 0) return;
    try {
      const key = 'compudopt_session_durations';
      const ring = JSON.parse(localStorage.getItem(key) || '[]');
      ring.push({ durationMs: Math.round(durationMs), endedAt: new Date().toISOString() });
      localStorage.setItem(key, JSON.stringify(ring.slice(-200)));
    } catch (e) {
      // localStorage unavailable - dashboard will show 'unavailable'
    }
  }

  /**
   * Read completed session durations for the admin dashboard.
   * @returns {Array<{durationMs: number, endedAt: string}>}
   */
  getCompletedSessions() {
    try {
      return JSON.parse(localStorage.getItem('compudopt_session_durations') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Track that user explored a flow
   * @param {string} flowId - Flow identifier (e.g., 'giveaway', 'donation', 'internet-help')
   */
  trackExploredFlow(flowId) {
    if (!flowId) return;

    const session = this.getSession();
    
    // Add to explored flows if not already present
    if (!session.exploredFlows.includes(flowId)) {
      session.exploredFlows.push(flowId);
    }

    // Add to journey path with timestamp
    session.journeyPath.push({
      type: 'flow',
      id: flowId,
      timestamp: new Date().toISOString()
    });

    this.saveSession(session);
    logger.log('[SessionTracking] Tracked flow:', flowId);
  }

  /**
   * Track that user selected an option from Explore More
   * @param {string} optionId - Option identifier
   */
  trackExploredOption(optionId) {
    if (!optionId) return;

    const session = this.getSession();
    
    // Add to explored options if not already present
    if (!session.exploredOptions.includes(optionId)) {
      session.exploredOptions.push(optionId);
    }

    // Add to journey path with timestamp
    session.journeyPath.push({
      type: 'option',
      id: optionId,
      timestamp: new Date().toISOString()
    });

    this.saveSession(session);
    logger.log('[SessionTracking] Tracked option:', optionId);
  }

  /**
   * Track Explore More button click
   * @param {string} fromFlow - Flow where Explore More was clicked
   */
  trackExploreMoreClick(fromFlow = null) {
    const session = this.getSession();
    session.exploreMoreClicks += 1;

    // Add to journey path
    session.journeyPath.push({
      type: 'explore_more',
      from: fromFlow,
      timestamp: new Date().toISOString()
    });

    this.saveSession(session);
    logger.log('[SessionTracking] Explore More clicked from:', fromFlow);
  }

  /**
   * Update conversation depth (number of messages exchanged)
   * @param {number} depth - Current conversation depth
   */
  updateConversationDepth(depth) {
    const session = this.getSession();
    session.conversationDepth = depth;
    this.saveSession(session);
  }

  /**
   * Set primary language
   * @param {string} language - Language code ('en' or 'es')
   */
  setPrimaryLanguage(language) {
    const session = this.getSession();
    session.primaryLanguage = language;
    this.saveSession(session);
  }

  /**
   * Filter options to exclude already explored ones
   * @param {Array} allOptions - All available options
   * @returns {Array} Options not yet explored
   */
  filterUnexplored(allOptions) {
    const session = this.getSession();
    const explored = new Set([
      ...session.exploredFlows,
      ...session.exploredOptions
    ]);

    // Filter out explored options, but always keep 'support-human' (removed 'return-home' as it's not useful)
    const fresh = allOptions.filter(option => {
      const isEssential = ['support-human'].includes(option.id);
      return isEssential || !explored.has(option.id);
    });

    // If we've filtered out everything (user explored all), return original options
    // This prevents a dead-end where no options are shown
    if (fresh.length < 2) {
      logger.log('[SessionTracking] All options explored, showing all again');
      return allOptions;
    }

    return fresh;
  }

  /**
   * Get unexplored options from a list
   * @param {Array} options - Array of option objects with 'id' property
   * @returns {Array} Unexplored options
   */
  getUnexploredOptions(options) {
    return this.filterUnexplored(options);
  }

  /**
   * Get user's journey path (for analytics and debugging)
   * @returns {Array} Journey path with timestamps
   */
  getJourneyPath() {
    const session = this.getSession();
    return session.journeyPath || [];
  }

  /**
   * Get the most recent meaningful flow from session history
   * Filters out non-meaningful flows like 'idle', 'exploring', etc.
   * @returns {string|null} Most recent flow ID, or null if none found
   */
  getLastFlow() {
    const IGNORED_FLOWS = new Set(['idle', 'exploring', null, undefined]);
    
    try {
      const session = this.getSession();
      
      // Prefer journeyPath with timestamps (most recent first)
      if (session.journeyPath && Array.isArray(session.journeyPath) && session.journeyPath.length > 0) {
        // Iterate backwards (most recent first)
        for (let i = session.journeyPath.length - 1; i >= 0; i--) {
          const entry = session.journeyPath[i];
          if (entry && entry.type === 'flow' && entry.id && !IGNORED_FLOWS.has(entry.id)) {
            return entry.id;
          }
        }
      }
      
      // Fallback to exploredFlows array (last item)
      if (session.exploredFlows && Array.isArray(session.exploredFlows) && session.exploredFlows.length > 0) {
        const lastFlow = session.exploredFlows[session.exploredFlows.length - 1];
        if (lastFlow && !IGNORED_FLOWS.has(lastFlow)) {
          return lastFlow;
        }
      }
      
      return null;
    } catch (e) {
      logger.warn('[SessionTracking] Error getting last flow (malformed session data):', e);
      return null;
    }
  }

  /**
   * Check if user is in "deep exploration" mode (explored 3+ topics)
   * @returns {boolean} True if user has explored 3 or more flows/options
   */
  isDeepExplorer() {
    const session = this.getSession();
    const totalExplored = (session.exploredFlows?.length || 0) + 
                          (session.exploredOptions?.length || 0);
    return totalExplored >= 3;
  }

  /**
   * Get exploration depth (total unique flows + options explored)
   * @returns {number} Exploration depth
   */
  getExplorationDepth() {
    const session = this.getSession();
    return (session.exploredFlows?.length || 0) + 
           (session.exploredOptions?.length || 0);
  }

  /**
   * Check if user has explored a specific flow
   * @param {string} flowId - Flow identifier
   * @returns {boolean} True if flow has been explored
   */
  hasExploredFlow(flowId) {
    const session = this.getSession();
    return session.exploredFlows?.includes(flowId) || false;
  }

  /**
   * Check if user has selected a specific option
   * @param {string} optionId - Option identifier
   * @returns {boolean} True if option has been selected
   */
  hasExploredOption(optionId) {
    const session = this.getSession();
    return session.exploredOptions?.includes(optionId) || false;
  }

  /**
   * Get session statistics (for analytics)
   * @returns {Object} Session stats
   */
  getSessionStats() {
    const session = this.getSession();
    return {
      sessionId: session.sessionId,
      duration: Date.now() - new Date(session.startTime).getTime(),
      exploredFlowsCount: session.exploredFlows?.length || 0,
      exploredOptionsCount: session.exploredOptions?.length || 0,
      exploreMoreClicks: session.exploreMoreClicks || 0,
      conversationDepth: session.conversationDepth || 0,
      isDeepExplorer: this.isDeepExplorer(),
      journeyLength: session.journeyPath?.length || 0
    };
  }

  /**
   * Clear session data (for testing or user-requested reset)
   */
  clearSession() {
    try {
      sessionStorage.removeItem(this.sessionKey);
      this.initializeSession();
      logger.log('[SessionTracking] Session cleared');
    } catch (e) {
      logger.warn('[SessionTracking] Error clearing session:', e);
    }
  }

  /**
   * Export session data for analytics
   * @returns {Object} Complete session data
   */
  exportSessionData() {
    return this.getSession();
  }
}

// Create singleton instance
const sessionTracker = new SessionTrackingService();

export default sessionTracker;
export { SessionTrackingService };

